import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Zap, BadgeCheck, Coins, Layers, MessagesSquare, Store, Trophy, MessageCircle, Eye, Wallet, PackageCheck, CheckCircle2, ChevronLeft, ChevronRight, Download, ArrowRight, Check, Bot, Sparkles, Rss } from 'lucide-react';
import Reveal from '../components/common/Reveal';
import { Icon } from '../components/common/ui';
import NuyIcon from '../components/common/NuyIcon';
import HeroSlider from '../components/home/HeroSlider';
import { openDrawer } from '../components/layout/ChatDrawer';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, timeAgo } from '../lib/format';
import { useStats, useListings, useResources, useThreads, useRankings, useCategories, usePublicSettings } from '../hooks/useApi';

function Rail({ children, testid }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: 'smooth' });
  return (
    <div className="relative group/rail">
      <button onClick={() => scroll(-1)} className="gx-rail-arrow left-0 -translate-x-1/2" aria-label="Scroll left" data-testid={`${testid}-prev`}><ChevronLeft className="w-5 h-5" /></button>
      <button onClick={() => scroll(1)} className="gx-rail-arrow right-0 translate-x-1/2" aria-label="Scroll right" data-testid={`${testid}-next`}><ChevronRight className="w-5 h-5" /></button>
      <div ref={ref} className="gx-hrail" data-testid={testid}>{children}</div>
    </div>
  );
}

function SectionHead({ title, to, cta, count }) {
  return (
    <div className="gx-section-head">
      <h2 className="gx-section-title">{title} {count != null && <span className="font-mono text-sm text-gx-muted font-normal ml-1">{formatNumber(count)}</span>}</h2>
      {to && <Link to={to} className="gx-section-link">{cta} <ArrowRight className="w-3.5 h-3.5" /></Link>}
    </div>
  );
}

const TILES = [
  { to: '/marketplace', icon: Store, color: '#19E58C', kind: 'Escrow', title: 'Marketplace', tag: 'hot', sub: 'Accounts, digital goods & services — protected by escrow.' },
  { to: '/resources', icon: Layers, color: '#6D28FF', kind: 'Downloads', title: 'Resources', sub: 'Themes, plugins, scripts and tools. Free & premium.' },
  { to: '/forums', icon: MessagesSquare, color: '#00E5FF', kind: 'Community', title: 'Forums', sub: 'Discuss, ask and share. Quality posts earn NUY.' },
  { to: '/wallet', icon: Wallet, color: '#FFC23B', kind: 'Economy', title: 'NUY Wallet', tag: 'new', sub: 'Buy, earn, spend and withdraw the native coin.' },
  { to: '/plus', icon: Sparkles, color: '#EC4899', kind: 'Membership', title: 'NUYED Plus', sub: 'Lower fees, zero ads, monthly NUY bonus.' },
  { to: '/feed', icon: Rss, color: '#2388FF', kind: 'Live', title: 'Feed & Chat', sub: 'Real-time community feed, chat rooms and NuyBot AI.', bot: true },
];

export default function Home() {
  const { isAuthed, openAuth } = useAuth();
  const { data: stats = {} } = useStats();
  const { data: site } = usePublicSettings();
  const { data: listingsData } = useListings({ sort: 'newest', per_page: 12, page: 1 });
  const { data: resourcesData } = useResources({ sort: 'recent', per_page: 12, page: 1 });
  const { data: threadsData } = useThreads({ tab: 'latest', page: 1 });
  const { data: rankings } = useRankings();
  const { data: mktCats } = useCategories('marketplace');

  const latestResources = resourcesData?.items || [];
  const latestListings = listingsData?.items || [];
  const recentThreads = (threadsData?.items || []).slice(0, 7);
  const topUsers = (rankings?.by_gax || []).slice(0, 7);
  const mktGroups = (mktCats || []).filter((c) => !c.parent_slug).slice(0, 12);
  const promoImg = latestResources[0]?.thumb_url || latestListings[0]?.cover;

  return (
    <>
      <HeroSlider intervalMs={site?.hero_interval_ms || 6000} />

      {/* PROMO + PLATFORM TILES */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.6fr)] gap-4 mb-12" data-testid="platform-section">
        <div className="gx-promo" data-testid="promo-card">
          {promoImg && <img src={promoImg} alt="" className="gx-promo-media" loading="lazy" />}
          <div className="gx-promo-shade" />
          <div className="gx-promo-body">
            <h2 className="gx-promo-title">{isAuthed ? <>Your community,<br /><span className="text-gx-cyan">your economy</span></> : <>Sign up and get<br /><span className="text-gx-cyan">50 NUY free</span></>}</h2>
            <ul className="gx-promo-list">
              {[isAuthed ? 'Escrow-protected trading in USD or NUY' : 'Welcome bonus credited instantly', 'Earn NUY for posts, reviews and sales', 'Withdraw to PayPal or crypto'].map((t) => (
                <li key={t}><Check className="w-3.5 h-3.5 text-gx-cyan flex-shrink-0" /> {t}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 pt-1">
              {isAuthed ? (
                <>
                  <Link to="/marketplace/new" className="gx-btn-primary" data-testid="promo-cta-sell">Sell a product <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/resources/new" className="gx-btn-ghost" data-testid="promo-cta-upload">Publish a resource</Link>
                </>
              ) : (
                <>
                  <button onClick={() => openAuth('register')} className="gx-btn-primary" data-testid="hero-cta-register">Create free account <ArrowRight className="w-4 h-4" /></button>
                  <Link to="/marketplace" className="gx-btn-ghost" data-testid="hero-cta-explore">Explore marketplace</Link>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="platform-tiles">
          {TILES.map((t) => {
            const Ic = t.icon;
            const body = (
              <>
                <div className="gx-tile-top">
                  <span className="gx-tile-ic" style={{ background: `${t.color}1a`, color: t.color }}>{t.title === 'NUY Wallet' ? <NuyIcon className="w-5 h-5" /> : <Ic className="w-4 h-4" />}</span>
                  <span className="gx-tile-kind">{t.bot ? <Bot className="w-3 h-3" /> : null}{t.kind}</span>
                </div>
                <div>
                  <p className="gx-tile-title">{t.title}{t.tag === 'hot' && <span className="gx-tag-hot">Top</span>}{t.tag === 'new' && <span className="gx-tag-new">New</span>}</p>
                  <p className="gx-tile-sub">{t.sub}</p>
                </div>
              </>
            );
            return t.bot
              ? <button key={t.title} type="button" onClick={() => openDrawer('bot')} className="gx-tile" data-testid={`tile-${t.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{body}</button>
              : <Link key={t.title} to={t.to} className="gx-tile" data-testid={`tile-${t.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{body}</Link>;
          })}
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="stats-strip">
        {[['Members', stats.users], ['Listings', stats.listings], ['Resources', stats.resources], ['Threads', stats.threads]].map(([lbl, val]) => (
          <div key={lbl} className="gx-kpi" data-testid={`hero-stat-${lbl.toLowerCase()}`}>
            <dt>{lbl}</dt>
            <dd className="text-white">{formatNumber(val || 0)}</dd>
          </div>
        ))}
      </section>

      {/* RESOURCES */}
      <section className="mb-12">
        <SectionHead title="Latest resources" to="/resources" cta="View all" count={stats.resources} />
        {latestResources.length > 0 ? (
          <Rail testid="resources-rail">
            {latestResources.map((r) => (
              <Link key={r.id} to={`/resources/${r.slug}`} className="gx-rail-card group" data-testid={`res-card-${r.slug}`}>
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0B1021]">
                  <img src={r.thumb_url} alt={r.title} className="w-full h-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                  <div className={`absolute top-2 left-2 text-[10px] font-display uppercase tracking-widest px-2 py-0.5 rounded-full backdrop-blur ${r.price_type === 'free' ? 'bg-emerald-500/25 text-emerald-300' : 'bg-gx-cyan/25 text-gx-cyan'}`}>{r.price_type === 'free' ? 'Free' : 'Paid'}</div>
                </div>
                <div className="p-3.5">
                  <p className="text-[10px] text-gx-muted uppercase tracking-widest font-display mb-1 truncate">{r.category_name}</p>
                  <h3 className="font-display font-bold text-sm text-white group-hover:text-gx-cyan transition leading-tight line-clamp-2 min-h-[2.4em]">{r.title}</h3>
                  <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-white/[0.05] text-xs text-gx-muted">
                    <span className="text-yellow-400">★ {r.rating_avg || 0}</span>
                    <span className="flex items-center gap-1 ml-auto"><Download className="w-3 h-3" />{formatNumber(r.downloads_count || 0)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </Rail>
        ) : <EmptyState icon={Layers} text="No resources published yet." cta="Upload the first resource" to="/resources/new" />}
      </section>

      {/* MARKETPLACE */}
      <section className="mb-12">
        <SectionHead title="Fresh on the marketplace" to="/marketplace" cta="Browse all" count={stats.listings} />
        {latestListings.length > 0 ? (
          <Rail testid="listings-rail">
            {latestListings.map((l) => (
              <Link key={l.id} to={`/marketplace/${l.slug}`} className="gx-rail-card group flex flex-col" data-testid={`listing-card-${l.slug}`}>
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0B1021]">
                  <img src={l.cover} className="w-full h-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" alt={l.title} />
                  <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase font-display tracking-widest bg-black/70 text-gx-cyan backdrop-blur rounded-full">{l.cat_name}</span>
                  {l.verified_seller && <span className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-emerald-500/90 text-black rounded-full">✓</span>}
                </div>
                <div className="p-3.5 flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-sm group-hover:text-gx-cyan transition line-clamp-2 leading-snug min-h-[2.4em]">{l.title}</h3>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05]">
                    <span className="font-mono font-bold text-base text-white">${Number(l.price_usd).toFixed(2)}</span>
                    <span className="text-[11px] text-gx-muted flex items-center gap-1.5"><img src={l.avatar_url} className="w-4 h-4 rounded-full" alt="" />@{l.username}</span>
                  </div>
                </div>
              </Link>
            ))}
          </Rail>
        ) : <EmptyState icon={Store} text="No listings yet." cta="Sell the first product" to="/marketplace/new" />}
        {mktGroups.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="mkt-cat-chips">
            {mktGroups.map((c) => (
              <Link key={c.slug} to={`/marketplace?node=${c.slug}`} className="gx-chip">
                <span style={{ color: c.color }}><Icon name={c.icon} className="w-3.5 h-3.5" /></span>{c.name}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* FORUMS + TOP HOLDERS */}
      <section className="mb-12">
        <SectionHead title="Community activity" to="/forums" cta="Open forums" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Reveal className="gx-bento lg:col-span-2 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
              <div className="flex items-center gap-2"><span className="gx-live-dot" /><h3 className="font-display font-bold text-sm">Recent threads</h3></div>
              <Link to="/forums/new" className="text-xs text-gx-cyan hover:underline">+ New thread</Link>
            </div>
            <div className="divide-y divide-white/[0.04]">
              {recentThreads.length > 0 ? recentThreads.map((t) => (
                <Link key={t.id} to={`/forums/thread/${t.slug}`} className="gx-thread-row flex items-center gap-3 px-5 py-3 group" style={{ '--accent': t.cat_color }}>
                  <img src={t.avatar_url} className="w-8 h-8 rounded-full ring-1 ring-white/10 flex-shrink-0" alt="" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold text-sm truncate group-hover:text-gx-cyan transition">{t.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gx-muted">
                      <span className="px-1.5 py-0.5 rounded-md font-display" style={{ background: `${t.cat_color}1f`, color: t.cat_color }}>{t.cat_name}</span>
                      <span>@{t.username}</span><span>· {timeAgo(t.last_reply_at)}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-[11px] text-gx-muted flex-shrink-0 font-mono">
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{t.replies_count}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{formatNumber(t.views_count)}</span>
                  </div>
                </Link>
              )) : <p className="text-sm text-gx-muted px-5 py-10 text-center">No threads yet. <Link to="/forums/new" className="text-gx-cyan hover:underline">Start the first one →</Link></p>}
            </div>
          </Reveal>
          <Reveal className="gx-bento overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
              <div className="flex items-center gap-2"><Trophy className="w-3.5 h-3.5 text-yellow-400" /><h3 className="font-display font-bold text-sm">Top NUY holders</h3></div>
              <Link to="/rankings" className="text-xs text-gx-cyan hover:underline">All →</Link>
            </div>
            <div className="p-2">
              {topUsers.length > 0 ? topUsers.map((u, i) => (
                <Link key={u.username} to={`/user/${u.username}`} className="flex items-center gap-3 group px-2.5 py-2 rounded-xl hover:bg-white/[0.04] transition">
                  <span className={`font-mono text-xs w-5 text-center ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gx-muted'}`}>{i + 1}</span>
                  <img src={u.avatar_url} className="w-7 h-7 rounded-full ring-1 ring-white/10" alt="" />
                  <p className="flex-1 min-w-0 font-display font-semibold text-sm group-hover:text-gx-cyan transition truncate">{u.username}</p>
                  <span className="font-mono text-xs text-white flex items-center gap-1.5"><NuyIcon className="w-3.5 h-3.5" />{formatNumber(u.gax_balance)}</span>
                </Link>
              )) : <p className="text-sm text-gx-muted px-3 py-8 text-center">No members ranked yet.</p>}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY / HOW */}
      <section className="mb-12" data-testid="features-section">
        <SectionHead title="Built for safe trading" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            [ShieldCheck, 'Escrow protected', 'Funds are held until both sides confirm. Disputes handled by staff.', '#00E5FF'],
            [Zap, 'Instant delivery', 'Auto-delivery for keys & credentials the second payment clears.', '#2388FF'],
            [BadgeCheck, 'Verified sellers', 'Trust levels, ratings and KYC badges on every listing.', '#19E58C'],
            [Coins, 'NUY economy', 'Earn for activity, spend across the platform, withdraw anytime.', '#6D28FF'],
          ].map(([Ic, t, d, c], i) => (
            <Reveal key={t} className="gx-tile" delay={i * 60}>
              <div className="gx-tile-top"><span className="gx-tile-ic" style={{ background: `${c}1a`, color: c }}><Ic className="w-4 h-4" /></span></div>
              <div><p className="gx-tile-title">{t}</p><p className="gx-tile-sub">{d}</p></div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ['01', Wallet, 'Pay into escrow', 'Your payment is held by NUYED — the seller never receives funds upfront.'],
            ['02', PackageCheck, 'Receive your product', 'Instant auto-delivery or manual hand-off, then inspect what you bought.'],
            ['03', CheckCircle2, 'Confirm & release', 'Happy? Release the funds. A problem? Open a dispute and staff steps in.'],
          ].map(([n, Ic, t, d]) => (
            <Reveal key={n} className="gx-bento p-5 relative">
              <span className="absolute top-4 right-5 font-mono text-xs text-gx-muted/60">{n}</span>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gx-purple/10 border border-gx-purple/25 text-gx-purple mb-4"><Ic className="w-4 h-4" /></div>
              <h3 className="font-display font-bold text-base mb-1">{t}</h3>
              <p className="text-sm text-gx-muted leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {!isAuthed && (
        <section className="mb-8">
          <div className="gx-bento p-8 md:p-12 text-center">
            <h2 className="font-display font-black text-3xl md:text-4xl mb-3 tracking-tight">Ready to join NUYED?</h2>
            <p className="text-gx-muted mb-7 max-w-xl mx-auto text-sm md:text-base">Create your free account and grab <span className="text-gx-cyan font-mono">50 NUY</span> as a welcome bonus — marketplace, resources and the full community, unlocked.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => openAuth('register')} className="gx-btn-primary" data-testid="bottom-cta-register">Create free account</button>
              <Link to="/marketplace" className="gx-btn-ghost">Browse the marketplace</Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function EmptyState({ icon: Ic, text, cta, to }) {
  return (
    <div className="gx-bento p-10 text-center" data-testid="empty-state">
      <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center bg-white/[0.04] border border-white/10 text-gx-muted mb-4"><Ic className="w-5 h-5" /></div>
      <p className="text-sm text-gx-muted mb-4">{text}</p>
      <Link to={to} className="gx-btn-primary inline-flex text-sm">{cta} <ArrowRight className="w-4 h-4" /></Link>
    </div>
  );
}
